import type { SlideRubric } from '../models/SlideRubric'
import type { RoomStateRubricResult } from '../models/RoomStateRubricResult'
import { type SlideModel } from '../models/SlideModel'

/**
 * Sorts rubrics by slide order, then alphabetically by rubric text
 */
export function sortRubrics(
  rubrics: SlideRubric[],
  slides: SlideModel[]
): SlideRubric[] {
  return [...rubrics].sort((a, b) => {
    const [aStartSlideId, aEndSlideId] = [a.data.startSlideId, a.data.slideId]
    const [bStartSlideId, bEndSlideId] = [b.data.startSlideId, b.data.slideId]

    const aStart = slides.findIndex((s) => s.id === aStartSlideId)
    const aEnd = slides.findIndex((s) => s.id === aEndSlideId)
    const bStart = slides.findIndex((s) => s.id === bStartSlideId)
    const bEnd = slides.findIndex((s) => s.id === bEndSlideId)

    // sort by end slide first (higher in ordered slide list comes first)
    if (aEnd !== bEnd) {
      if (!aEndSlideId) return -1 // all slides comes first
      if (!bEndSlideId) return 1
      return aEnd - bEnd
    }

    // then by start slide (higher in ordered slide list comes first)
    if (aStart !== bStart) return aStart - bStart

    // finally alphabetically by rubric text
    return a.data.rubric.localeCompare(b.data.rubric)
  })
}

/**
 * Sorts rubrics alphabetically by rubric text, ignoring slide position. For
 * rubric sets that span the whole experience (no start or end slide), where
 * the slide tiers of `sortRubrics` all tie and the name is the only signal.
 */
export function sortRubricsByName(rubrics: SlideRubric[]): SlideRubric[] {
  return sortRubrics(rubrics, [])
}

/**
 * Sorts rubric results based on the same ordering as the rubrics themselves
 */
export function sortRubricResults(
  rubricResults: RoomStateRubricResult[],
  rubrics: SlideRubric[],
  slides: SlideModel[]
): RoomStateRubricResult[] {
  const rubricOrderMap = new Map<string, number>()

  sortRubrics(rubrics, slides).forEach((rubric, index) => {
    rubricOrderMap.set(rubric.id, index)
  })

  return [...rubricResults].sort((a, b) => {
    const aOrder = rubricOrderMap.get(a.rubricId) ?? Number.MAX_SAFE_INTEGER
    const bOrder = rubricOrderMap.get(b.rubricId) ?? Number.MAX_SAFE_INTEGER
    return aOrder - bOrder
  })
}
